import {
  EngSpecCommit,
  EngSpecFileDiff,
  EngSpecJson,
} from "@/components/AI/EngSpec/EngSpec";
import { PRDVisualContext } from "@/components/AI/PRD/PRD";
import { Database } from "@/utils/supabase.types";
import { useMemo } from "react";
import { create } from "zustand";
import { ChatContextCodeSnippet } from "./aiContextStore";
import { useCanvasId } from "@/utils/utils";
import { UploadedFile } from "@/utils/types";
import { track } from "@/utils/events/tracker";
import { SupabaseClient } from "@supabase/supabase-js";

export const FULL_CODEBASE_CONSTANT = "___FULL_CODEBASE_DESCRIPTION___";
export const MESSAGES_BATCH_SIZE = 50; // Number of messages to load per batch
export const FALLBACK_BATCH_SIZE = 10; // Smaller batch size to use when the initial request fails
export const MESSAGES_PER_CONVERSATION = 15; // Initial number of messages to display per conversation

export type DBLoadStatus = "idle" | "loading" | "succeeded" | "error";

export type FeaturePrd = Omit<
  Database["public"]["Tables"]["feature_prd"]["Row"],
  "canvas_visual_context"
> & {
  canvas_visual_context?: PRDVisualContext;
};
export type DataKey =
  | "aiChatConversations"
  | "aiChatMessages"
  | "savedStoryboardHtml";

export type ChangeApplyStatus =
  | { status: "pending" }
  | { status: "in_progress" }
  | { status: "succeeded" }
  | {
      status: "failed";
      reason: string;
      fixing: boolean;
      isOutputTokenLimitHit?: boolean;
      continuationPrompt?: string;
    };

export type MessageContext = {
  commit?: Partial<EngSpecCommit> | any;
  file?: Partial<EngSpecFileDiff>;
  plan?: Partial<EngSpecJson> | any;
  codeSnippet?: ChatContextCodeSnippet;
  diffCodeSnippet?: ChatContextCodeSnippet;

  // If the user is asking to make a new item
  makeNew?: {
    type: "commit" | "file";
    prId?: string;
    commitId?: string;
  };

  applied_segment_indices?: number[];

  // If there is a change this is the activity id
  savedActivityId?: string;

  // Directly part of the message
  // imageUrls is deprecated as of 2025-03-21, use files instead
  imageUrls?: string[];
  files?: UploadedFile[];
  totalUserSelection?: any[]; // Selected storyboards or elements, if storyboard then the object will have isStoryboard set to true

  // For the PRD view
  previousPrd?: string;

  finished?: boolean;
  hadError?: boolean;
  chatOnlyMode?: boolean;
  filesWithErrors?: string[];

  // To track which files were edited in this message
  editedFiles?: string[];

  dbChanges?: EngSpecFileDiff[];

  // The apply status of each change in this message, introduced starting from March 17th 2025, old messages will not have this.
  changeApplyStatus?: {
    // Convention: there is only one runSqlMigration, deployEdgeFunction, runConvexSync change per message
    runSqlMigration?: ChangeApplyStatus;
    deployEdgeFunction?: ChangeApplyStatus;
    runConvexSync?: ChangeApplyStatus;
    terminalCommands?: {
      [terminalCommand: string]: ChangeApplyStatus;
    };
    fileChanges?: {
      [filePath: string]: ChangeApplyStatus;
    };
    storyboardChanges?: {
      [storyboardName: string]: ChangeApplyStatus;
    };
  };
  envVariablesSet?: string[];

  // AI usage statistics
  promptsConsumed?: number;
  toolCallsCount?: number;
  inputTokens?: number;
  outputTokens?: number;
};

export type Conversation =
  Database["public"]["Tables"]["ai_chat_conversations"]["Row"];
export type Message = Omit<
  Database["public"]["Tables"]["ai_chat_messages"]["Row"],
  "message_context"
> & {
  message_context?: MessageContext;
  chat_only_mode?: boolean;
};
export type StoryboardSavedHtml =
  Database["public"]["Tables"]["saved_storyboard_html"]["Row"];

export interface SupabaseStore {
  supabaseClient?: SupabaseClient;
  setSupabaseClient: (supabaseClient: SupabaseClient) => void;

  aiChatConversations: { [id: string]: Conversation };
  aiChatConversationsStatus: DBLoadStatus;

  aiChatMessages: { [id: string]: Message };
  aiChatMessagesStatus: DBLoadStatus;

  savedStoryboardHtml: { [id: string]: StoryboardSavedHtml };
  savedStoryboardHtmlStatus: DBLoadStatus;

  // Hooks from the database sync wrapper
  setAllValues: (dataKey: DataKey, data: any) => void;
  onDbSnapshot: (
    dataKey: DataKey,
    payload: { newItems: any[]; deletedItems: any[] } | any,
    largeFields?: string[],
    onConflictField?: string
  ) => void;
  setLoading: (dataKey: DataKey) => void;

  aiChatMessagesPagination: {
    [conversationId: string]: {
      hasMore: boolean;
      lastMessageTimestamp: string | null;
      isLoading: boolean;
      noMoreMessages: boolean;
    };
  };

  loadMoreMessages: (
    conversationId: string,
    supabaseClient: any
  ) => Promise<void>;
  resetMessages: (conversationId: string) => void;
}

export const useSupabaseStore = create<SupabaseStore>((set, get) => ({
  supabaseClient: undefined,
  setSupabaseClient: (supabaseClient: SupabaseClient) => {
    set({ supabaseClient });
  },

  aiChatConversations: {},
  aiChatConversationsStatus: "idle",

  aiChatMessages: {},
  aiChatMessagesStatus: "idle",

  savedStoryboardHtml: {},
  savedStoryboardHtmlStatus: "idle",

  aiChatMessagesPagination: {},

  loadMoreMessages: async (conversationId: string, supabaseClient: any) => {
    const state = get();
    const pagination = state.aiChatMessagesPagination[conversationId];

    if (!pagination || pagination.isLoading || !pagination.hasMore) {
      return;
    }

    set((state) => ({
      aiChatMessagesPagination: {
        ...state.aiChatMessagesPagination,
        [conversationId]: {
          ...pagination,
          isLoading: true,
        },
      },
    }));

    let usedBatchSize = MESSAGES_BATCH_SIZE;

    try {
      if (!supabaseClient) {
        return;
      }

      const tryLoadMessages = async (batchSize: number) => {
        let query = supabaseClient
          .from("ai_chat_messages")
          .select("*")
          .eq("conversation_id", conversationId)
          .order("created_at", { ascending: false })
          .limit(batchSize);

        if (pagination.lastMessageTimestamp) {
          query = query.lt("created_at", pagination.lastMessageTimestamp);
        }

        return await query;
      };

      let { data: messages, error } = await tryLoadMessages(usedBatchSize);

      if (error) {
        track("AI_CHAT_WARNING", {
          warning: `Retrying to load chat history with smaller batch size`,
          data: {
            conversationId,
            error,
            usedBatchSize,
          },
        });
        usedBatchSize = FALLBACK_BATCH_SIZE;
        const retryResult = await tryLoadMessages(usedBatchSize);
        messages = retryResult.data;
        error = retryResult.error;
      }

      if (error) {
        track("AI_CHAT_ERROR", {
          error: `Error loading more messages`,
          data: {
            conversationId,
            error,
            usedBatchSize,
          },
        });

        set((state) => ({
          aiChatMessagesPagination: {
            ...state.aiChatMessagesPagination,
            [conversationId]: {
              ...pagination,
              isLoading: false,
            },
          },
        }));
        return;
      }

      if (messages && messages.length > 0) {
        set((state) => ({
          aiChatMessages: {
            ...state.aiChatMessages,
            ...messages.reduce((acc, msg) => {
              acc[msg.id] = msg;
              return acc;
            }, {}),
          },
          aiChatMessagesPagination: {
            ...state.aiChatMessagesPagination,
            [conversationId]: {
              hasMore: messages.length === usedBatchSize,
              lastMessageTimestamp: messages[messages.length - 1].created_at,
              isLoading: false,
              noMoreMessages: messages.length < usedBatchSize,
            },
          },
        }));
      } else {
        set((state) => ({
          aiChatMessagesPagination: {
            ...state.aiChatMessagesPagination,
            [conversationId]: {
              ...pagination,
              hasMore: false,
              isLoading: false,
              noMoreMessages: true,
            },
          },
        }));
      }
    } catch (error) {
      track("AI_CHAT_ERROR", {
        error: `Error loading more messages`,
        data: {
          conversationId,
          error,
          usedBatchSize,
        },
      });
      set((state) => ({
        aiChatMessagesPagination: {
          ...state.aiChatMessagesPagination,
          [conversationId]: {
            ...pagination,
            isLoading: false,
          },
        },
      }));
    }
  },

  resetMessages: (conversationId: string) => {
    set((state) => ({
      aiChatMessagesPagination: {
        ...state.aiChatMessagesPagination,
        [conversationId]: {
          hasMore: true,
          lastMessageTimestamp: null,
          isLoading: false,
          noMoreMessages: false,
        },
      },
    }));
  },
  setAllValues: (dataKey: DataKey, data: any[]) => {
    set({
      [dataKey]:
        data?.reduce((acc, row) => {
          acc[row.id] = row;
          return acc;
        }, {}) || {},
      [dataKey + "Status"]: "succeeded",
    });
  },
  setLoading: (dataKey: DataKey) =>
    set({
      [dataKey + "Status"]: "loading",
    }),
  onDbSnapshot: (
    dataKey: DataKey,
    payload: {
      newItems: any[];
      deletedItems: any[];
    },
    largeFields?: string[],
    onConflictField?: string
  ) => {
    const conflictField = onConflictField || "id";

    const newRows = { ...get()[dataKey] };
    payload.newItems?.forEach((row: any) => {
      const existingRow = Object.values(newRows).find(
        (r: any) => r[conflictField] === row[conflictField]
      );

      // Row already exists, update it
      if (existingRow) {
        // For large fields, sometimes in snapshots the value doesn't come back,
        // do not delete these values
        largeFields?.forEach((field: string) => {
          if (existingRow[field] && !row[field]) {
            row[field] = existingRow[field];
          }
        });
        const rowToSet = { ...existingRow, ...row };
        newRows[rowToSet.id] = rowToSet;
        return;
      }

      // New row, set it
      newRows[row.id] = row;
    });
    payload.deletedItems?.forEach((row: any) => {
      delete newRows[row.id];
    });
    set({
      [dataKey]: newRows,
    });
  },
}));
